/**
 * Appearance cookies — the one place their names, values and attributes live.
 *
 * Spec: specs/active/2026-08-29-website-design-system-adoption (Phase 2, Rev 3).
 * The pre-paint script in public/index.html (id="ips-appearance-boot") cannot
 * import this module, so it repeats the names and attributes by hand; the
 * head-script test pins the two together.
 *
 * Functional cookies (owner decision, spec §9 Q2): set without consent, and only
 * on an explicit choice in the appearance control. Attributes: host-only (no
 * Domain), Path=/, one year, SameSite=Lax, Secure on https. The repo's
 * utils/cookieManagement/setCookie is NOT used: it is always `secure` (breaks
 * http://localhost) and sets neither SameSite nor Max-Age.
 *
 * Every read is WHITELISTED: a sibling *.ipscore.app host can toss a cookie of
 * the same name onto this one, so any value other than the known ones is
 * treated as "no choice made".
 */

export const COLOR_MODE_COOKIE = "chakra-ui-color-mode";
export const STYLE_COOKIE = "ips-style";

export type AppearanceMode = "light" | "dark";
export type AppearanceStyle = "calm" | "expressive";

export const isAppearanceMode = (value: unknown): value is AppearanceMode =>
  value === "light" || value === "dark";

const ONE_YEAR_SECONDS = 31536000;

/** The LAST cookie of that name wins (same rule as the pre-paint script). */
export const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  const hits = document.cookie
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part.startsWith(prefix));
  return hits.length === 0 ? undefined : hits[hits.length - 1].slice(prefix.length);
};

export const cookieAttributes = (): string => {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  return `; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
};

export const writeCookie = (name: string, value: string): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${value}${cookieAttributes()}`;
};
